import React, { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import { UserFeedFollowGroupFragment } from 'api';
import { GroupAvatar, Numeric, PrivateGroupIcon, TableCellWithOverflow } from 'components';
import { generateLink } from 'Links';

import Loader from './Loader';

interface IProps {
  sentinelRef: (node: Element) => void;
  results: UserFeedFollowGroupFragment[];
  checkedIds: string[];
  isAllChecked: boolean;
  isExhausted: boolean;
  isInitialLoad: boolean;
  onAllChecked: (event: ChangeEvent<HTMLInputElement>) => void;
  onChecked: (event: ChangeEvent<HTMLInputElement>) => void;
}

const NAME_CELL_WIDTH = 400;

const AdminGroupTable: FC<IProps> = ({
  sentinelRef,
  results,
  checkedIds,
  isAllChecked,
  isExhausted,
  isInitialLoad,
  onAllChecked,
  onChecked,
}) => {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox checked={isAllChecked} onChange={onAllChecked} />
          </TableCell>
          <TableCell style={{ width: NAME_CELL_WIDTH }}>{t('common:name')}</TableCell>
          <TableCell>{t('common:department')}</TableCell>
          <TableCell>{t('common:location')}</TableCell>
          <TableCell>{t('common:member_plural')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {isInitialLoad && <Loader sentinelRef={null} rows={8} />}
        {results.map(result => (
          <TableRow key={result.id}>
            <TableCell padding="checkbox">
              <Checkbox
                name={result.id}
                checked={checkedIds.includes(result.id)}
                onChange={onChecked}
              />
            </TableCell>
            <TableCellWithOverflow>
              <Grid container alignItems="center" wrap="nowrap" spacing={2}>
                <Grid item>
                  <GroupAvatar size={50} link group={result as any} />
                </Grid>
                {result.is_private && (
                  <Grid item>
                    <PrivateGroupIcon size="small" group={result as any} type="list" />
                  </Grid>
                )}
                <Grid item container zeroMinWidth>
                  <Link
                    title={result.title}
                    component={generateLink(`/group/${result.id}`)}
                    variant="subtitle2"
                    noWrap
                  >
                    {result.title}
                  </Link>
                </Grid>
              </Grid>
            </TableCellWithOverflow>
            <TableCell>
              <Typography noWrap title={result.department_title}>
                {result.department_title}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap title={result.location_title}>
                {result.location_title}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography noWrap>
                <Numeric value={result.follower_count} />
              </Typography>
            </TableCell>
          </TableRow>
        ))}
        {!isExhausted && <Loader sentinelRef={sentinelRef} rows={1} />}
      </TableBody>
    </Table>
  );
};

AdminGroupTable.defaultProps = {
  results: [],
};

export default AdminGroupTable;
